import { Column, Entity, Index, JoinColumn, ManyToOne } from 'typeorm';
import { BaseEntity } from './base.entity';
import { ClientAddressEntity } from './client-address.entity';
import { ClientEntity } from './client.entity';
import { SalesTripEntity } from './sales-trip.entity';

/**
 * Outcome-only stop status (decision 16 — no arrived / in-progress):
 *   planned     — on the plan, nothing recorded yet
 *   visited     — a visit row exists and is linked (`clientVisitId`)
 *   skipped     — the rep chose not to go (reason optional)
 *   missed      — went / could not complete (reason required)
 *   rescheduled — moved to a stop on another trip (`rescheduledToStopId`)
 */
export type SalesTripStopStatus =
	| 'planned'
	| 'visited'
	| 'skipped'
	| 'missed'
	| 'rescheduled';

/**
 * Reasons mirror `trip_commit_skip.reason`, stored as a varchar so new reasons
 * need no enum migration.
 */
export const SALES_TRIP_STOP_OUTCOME_REASONS = [
	'not_available',
	'closed',
	'ran_out_of_time',
	'customer_request',
	'other',
] as const;
export type SalesTripStopOutcomeReason =
	(typeof SALES_TRIP_STOP_OUTCOME_REASONS)[number];

/**
 * One client on one sales trip (PD-15920). Times follow the trip's timezone
 * model: `plannedStart` is UTC, rendered in `sales_trip.timezone`.
 *
 * Timing model (decisions 14, 23): `isPinned` = a committed appointment the
 * optimizer and recalculation never move; a fluid stop's start is computed
 * from the previous stop's end + drive time. Drag / duration edits mark
 * `timesStale`; "Recalculate times" and "Optimize this day" clear it.
 */
@Entity('sales_trip_stop')
@Index('ix_sales_trip_stop_trip_sequence', ['salesTripId', 'sequence'])
@Index('ix_sales_trip_stop_client', ['clientId'])
export class SalesTripStopEntity extends BaseEntity {
	@Column({ name: 'fk_sales_trip__id', type: 'int', unsigned: true })
	public salesTripId!: number;

	@Column({ name: 'fk_client__id', type: 'int', unsigned: true })
	public clientId!: number;

	/** Defaults to the client's shipping address when null. */
	@Column({
		name: 'fk_client_address__id',
		type: 'int',
		unsigned: true,
		nullable: true,
	})
	public clientAddressId?: number | null;

	/** Provenance when the trip was built from a route. */
	@Column({
		name: 'fk_sales_route_client__id',
		type: 'int',
		unsigned: true,
		nullable: true,
	})
	public salesRouteClientId?: number | null;

	@Column({ name: 'sequence', type: 'int' })
	public sequence!: number;

	/** UTC. Null until the chain has been computed. */
	@Column({ name: 'plannedStart', type: 'datetime', nullable: true })
	public plannedStart?: Date | null;

	/**
	 * Copied at creation from `sales_route_client.stopDurationMinutes` or the
	 * tenant's sales default (30); editable per stop in 15-minute steps.
	 */
	@Column({ name: 'plannedDurationMinutes', type: 'int', default: 30 })
	public plannedDurationMinutes!: number;

	/**
	 * PD-16165 — the IANA zone of where this stop IS, derived from its
	 * coordinates by the server on the first recalculation after the stop is
	 * written (null until then, and for a stop with no map position). Its
	 * times are read and pinned in this zone rather than the trip's, so a day
	 * that crosses a zone line shows each stop in its own local time.
	 */
	@Column({ name: 'timezone', type: 'varchar', length: 64, nullable: true })
	public timezone?: string | null;

	@Column({ name: 'timesStale', type: 'boolean', default: false })
	public timesStale!: boolean;

	@Column({ name: 'isPinned', type: 'boolean', default: false })
	public isPinned!: boolean;

	@Column({
		name: 'estimatedDriveMinutesFromPrev',
		type: 'int',
		nullable: true,
	})
	public estimatedDriveMinutesFromPrev?: number | null;

	@Column({ name: 'status', type: 'varchar', length: 20, default: "'planned'" })
	public status!: SalesTripStopStatus;

	@Column({
		name: 'outcomeReason',
		type: 'varchar',
		length: 64,
		nullable: true,
	})
	public outcomeReason?: string | null;

	@Column({ name: 'outcomeDetail', type: 'text', nullable: true })
	public outcomeDetail?: string | null;

	/** Set when status = visited; the visit row carries `fk_sales_trip_stop__id` back. */
	@Column({
		name: 'fk_client_visit__id',
		type: 'int',
		unsigned: true,
		nullable: true,
	})
	public clientVisitId?: number | null;

	/** The stop created on a later trip when this one was rescheduled. */
	@Column({
		name: 'fk_sales_trip_stop__id_rescheduled_to',
		type: 'int',
		unsigned: true,
		nullable: true,
	})
	public rescheduledToStopId?: number | null;

	@Column({ name: 'notes', type: 'text', nullable: true })
	public notes?: string | null;

	@ManyToOne(() => SalesTripEntity, (trip) => trip.stops)
	@JoinColumn({ name: 'fk_sales_trip__id' })
	public fkSalesTrip!: SalesTripEntity;

	// Optional in TS because the relation is only present when explicitly
	// loaded (and is null when the client has been soft-deleted).
	@ManyToOne(() => ClientEntity)
	@JoinColumn({ name: 'fk_client__id' })
	public fkClient?: ClientEntity | null;

	@ManyToOne(() => ClientAddressEntity, { nullable: true })
	@JoinColumn({ name: 'fk_client_address__id' })
	public fkClientAddress?: ClientAddressEntity | null;
}
